import { useEffect, useRef, useState } from 'react';
import { ArrowUpRight, Menu, X } from 'lucide-react';
import { siteLinks } from '../../../data/events';

const sections = [
  { label: 'Upcoming Events', href: '#upcoming' },
  { label: 'On the Horizon', href: '#horizon' },
  { label: 'Become a Partner', href: '#partner' },
];

export function SiteHeader() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > Math.min(560, window.innerHeight - 80));
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    if (!open) return;

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false);
    };

    const onPointerDown = (event: PointerEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setOpen(false);
      }
    };

    window.addEventListener('keydown', onKeyDown);
    window.addEventListener('pointerdown', onPointerDown);
    return () => {
      window.removeEventListener('keydown', onKeyDown);
      window.removeEventListener('pointerdown', onPointerDown);
    };
  }, [open]);

  const darkButton = scrolled || open;

  return (
    <header className="fixed inset-x-0 top-0 z-40" aria-label="Site navigation">
      <div ref={menuRef} className="w-fit px-4 py-4 sm:px-5 sm:py-5">
        <button
          type="button"
          onClick={() => setOpen((value) => !value)}
          aria-label={open ? 'Close menu' : 'Open menu'}
          aria-expanded={open}
          aria-controls="site-menu"
          className={
            'flex h-11 w-11 items-center justify-center rounded-sm border backdrop-blur-sm transition-colors ' +
            (darkButton
              ? 'border-line bg-card text-fg-primary hover:bg-subtle'
              : 'border-white/45 bg-black/10 text-white hover:bg-white/15')
          }
        >
          {open ? <X size={20} strokeWidth={1.75} /> : <Menu size={20} strokeWidth={1.75} />}
        </button>

        {open && (
          <nav
            id="site-menu"
            aria-label="FMC Events"
            className="mt-3 w-[min(20rem,calc(100vw-2rem))] overflow-hidden rounded-md border border-line bg-card"
            style={{ boxShadow: 'var(--app-shadow-soft)' }}
          >
            <p className="px-6 pb-2 pt-5 font-display text-xl font-bold text-fg-primary">FMC Events</p>
            <div className="p-2">
              {sections.map((section) => (
                <a
                  key={section.href}
                  href={section.href}
                  onClick={() => setOpen(false)}
                  className="block rounded-sm px-4 py-2.5 font-ui text-sm font-semibold text-fg-primary hover:bg-subtle"
                >
                  {section.label}
                </a>
              ))}
            </div>

            <div className="border-t border-line-subtle p-2">
              <p className="px-4 pb-1.5 pt-2 font-ui text-xs font-semibold uppercase tracking-[0.1em] text-fg-muted">
                Event websites
              </p>
              {siteLinks.map((link) => (
                <a
                  key={link.href}
                  href={link.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => setOpen(false)}
                  className="flex items-center justify-between gap-3 rounded-sm px-4 py-2.5 font-body text-sm text-fg-secondary hover:bg-subtle"
                >
                  {link.label}
                  <ArrowUpRight size={15} strokeWidth={1.75} className="shrink-0 text-fg-muted" />
                </a>
              ))}
            </div>
          </nav>
        )}
      </div>
    </header>
  );
}

